import { useSuspenseQuery } from "@apollo/client";
import type {
  DataTableToolbarProps,
  FacetedFilterOption,
  GetOptionsByValueBase,
  OptionsByValue,
} from "@repo/ui";
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  DataTableViewOptions,
  FacetedFilterStandalone,
  PrimaryFullScreenSpinner,
} from "@repo/ui";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef, ColumnFiltersState } from "@tanstack/react-table";
import { X } from "lucide-react";
import { DateTime } from "luxon";
import { Suspense, useMemo } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { CreateProductButton } from "~/components/Product/CreateProductModal";
import { RateSourceBadge } from "~/components/Product/RateSourceBadge";
import { graphql } from "~/gql/client";
import type { AdminProductsQuery } from "~/gql/client/graphql";
import possibleTypes from "~/gql/client/possibleTypes";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/products")({
  head: () => opsHead("Products"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const { productId } = useParams({ strict: false });

  const adminProductsDocument = graphql(`
    query AdminProducts {
      products {
        id
        internalName
        provider {
          id
          name
        }
        liquidityInDays
        apy
        available
        rateScrapingEnabled
        ratesUpdatedAt
        __typename
      }
    }
  `);

  const { data } = useSuspenseQuery(adminProductsDocument);

  const initialColumnFilterState: ColumnFiltersState = [
    {
      id: "available",
      value: ["Yes"],
    },
  ].filter(({ value }) => value.length > 0);

  return (
    <AdminPageContainer testId="admin-products">
      <AdminPageHeader
        title="Products"
        subtitle="These are the products in Primary's codebase, which assets may use and plans may incorporate."
        buttons={
          <>
            <Link to="/yieldhub-sync">
              <Button variant="outline">Sync Yieldhub</Button>
            </Link>
            <CreateProductButton />
          </>
        }
      />
      <DataTable
        data={[...(data?.products ?? [])].sort((a, b) =>
          (a.internalName ?? "").localeCompare(b.internalName ?? ""),
        )}
        columns={columns}
        toolbarComponent={FiltersToolbar}
        activeRowId={productId}
        onRowClick={(row) => {
          const active = row.original.id === productId;

          return active
            ? navigate({ to: "/products" })
            : navigate({
                to: "/products/$productId",
                params: { productId: row.original.id },
              });
        }}
        initialColumnVisibility={{
          provider: false,
          kind: false,
          liquidityInDays: false,
        }}
        initialState={{
          columnFilters: initialColumnFilterState,
          pagination: {
            pageSize: 50,
          },
          sorting: [
            {
              id: "internalName",
              desc: false,
            },
          ],
        }}
        className="overflow-hidden border"
      />
    </AdminPageContainer>
  );
}

type ProductRow = NonNullable<AdminProductsQuery["products"]>[number];

const columns: ColumnDef<ProductRow>[] = [
  {
    id: "internalName",
    accessorFn(row) {
      return row.internalName;
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Internal name" />
    ),
  },
  {
    id: "provider",
    accessorFn: (row) => row.provider?.name ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Provider" />
    ),
    filterFn: (row, _id, value) =>
      value.includes(row.original.provider?.name ?? ""),
  },
  {
    id: "kind",
    accessorFn: (row) => row.__typename,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Kind" />
    ),
    cell: ({ row }) => friendlyProductTypename(row.original.__typename),
    filterFn: (row, _id, value) => value.includes(row.original.__typename),
  },
  {
    id: "liquidityInDays",
    accessorFn: (row) => row.liquidityInDays,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Liquidity" />
    ),
    cell: ({ row }) =>
      row.original.liquidityInDays
        ? `${row.original.liquidityInDays} day${
            row.original.liquidityInDays > 1 ? "s" : ""
          }`
        : "-",
  },
  {
    id: "baseRate",
    accessorFn: (row) => row.apy,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Base rate" />
    ),
    cell: ({ row }) =>
      row.original.apy ? (
        <>{row.original.apy.toDecimalPlaces(2).toString()}%</>
      ) : (
        <>-</>
      ),
  },
  {
    id: "rateSource",
    accessorFn: (row) => row.rateScrapingEnabled,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Rate source" />
    ),
    cell: ({ row }) => (
      <RateSourceBadge rateScrapingEnabled={row.original.rateScrapingEnabled} />
    ),
  },
  {
    id: "ratesUpdatedAt",
    accessorFn: (row) => row.ratesUpdatedAt?.getTime() ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Rates last updated" />
    ),
    cell: ({ row }) =>
      row.original.ratesUpdatedAt
        ? DateTime.fromJSDate(row.original.ratesUpdatedAt).toLocaleString(
            DateTime.DATE_MED,
          )
        : "-",
  },
  {
    id: "available",
    accessorFn(row) {
      return row.available ? "Yes" : "No";
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Available" />
    ),
    filterFn: (row, _id, value) =>
      value.includes(row.original.available ? "Yes" : "No"),
  },
];

type GetOptionsByValue = GetOptionsByValueBase<ProductRow>;

function FiltersToolbar({ table }: DataTableToolbarProps<ProductRow>) {
  const isFiltered =
    table.getPreFilteredRowModel().rows.length >
    table.getFilteredRowModel().rows.length;

  const preFilteredRows = table.getPreFilteredRowModel().rows;

  const providerOptionsByValue = useMemo<OptionsByValue>(
    () => getProviderOptionsByValue({ preFilteredRows }),
    [preFilteredRows],
  );

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 flex-wrap items-center gap-1">
        {table.getColumn("provider") && (
          <FacetedFilterStandalone
            column={table.getColumn("provider")}
            title="Provider"
            options={Object.values(providerOptionsByValue)}
          />
        )}
        {table.getColumn("kind") && (
          <FacetedFilterStandalone
            column={table.getColumn("kind")}
            title="Kind"
            options={kindOptions}
          />
        )}

        {table.getColumn("available") && (
          <FacetedFilterStandalone
            column={table.getColumn("available")}
            title="Available"
            options={isAvailableOptions}
          />
        )}

        {isFiltered && (
          <Button
            rightIcon={<X />}
            variant="ghost"
            onClick={() => table.resetColumnFilters()}
            className="h-8 bg-white px-2 lg:px-3"
          >
            Reset
          </Button>
        )}
      </div>
      <DataTableViewOptions table={table} />
    </div>
  );
}

const isAvailableOptions: FacetedFilterOption[] = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const kindOptions: FacetedFilterOption[] =
  possibleTypes.possibleTypes.Product.map((tn) => ({
    label: friendlyProductTypename(tn),
    value: tn,
  }));

const getProviderOptionsByValue: GetOptionsByValue = ({ preFilteredRows }) =>
  preFilteredRows.reduce(
    (acc, row) => {
      const providerName = row.original.provider?.name;

      if (providerName && !acc[providerName]) {
        acc[providerName] = {
          label: providerName,
          value: providerName,
        };
      }
      return acc;
    },
    {} as Record<string, FacetedFilterOption>,
  );

function friendlyProductTypename(s: string): string {
  switch (s) {
    case "FundProduct":
      return "Fund";
    case "BankAccountProduct":
      return "Bank Account";
    case "TermDepositProduct":
      return "Term Deposit";
    default:
      return s;
  }
}
